import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronRightIcon, EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef } from 'react';
import Link from '@/components/common/Link';

// Breadcrumb container
export interface BreadcrumbProps extends ComponentPropsWithoutRef<'nav'> {
    children: ReactNode;
}

const Breadcrumb = forwardRef<HTMLElement, BreadcrumbProps>(({ className, ...props }, ref) => (
    <nav ref={ref} aria-label="Breadcrumb" className={clsx('flex', className)} {...props} />
));
Breadcrumb.displayName = 'Breadcrumb';

// Breadcrumb list
export interface BreadcrumbListProps extends ComponentPropsWithoutRef<'ol'> {
    children: ReactNode;
}

const BreadcrumbList = forwardRef<HTMLOListElement, BreadcrumbListProps>(({ className, ...props }, ref) => (
    <ol
        ref={ref}
        className={clsx('text-muted-foreground flex flex-wrap items-center gap-1.5 text-sm break-words', className)}
        {...props}
    />
));
BreadcrumbList.displayName = 'BreadcrumbList';

// Breadcrumb item
export interface BreadcrumbItemProps extends ComponentPropsWithoutRef<'li'> {
    children: ReactNode;
}

const BreadcrumbItem = forwardRef<HTMLLIElement, BreadcrumbItemProps>(({ className, ...props }, ref) => (
    <li ref={ref} className={clsx('inline-flex items-center gap-1.5', className)} {...props} />
));
BreadcrumbItem.displayName = 'BreadcrumbItem';

// Breadcrumb link
export interface BreadcrumbLinkProps extends ComponentPropsWithoutRef<typeof Link> {
    children: ReactNode;
    asChild?: boolean;
}

const BreadcrumbLink = forwardRef<HTMLAnchorElement, BreadcrumbLinkProps>(
    ({ className, asChild = false, ...props }, ref) => {
        const Comp = asChild ? 'span' : Link;
        return <Comp ref={ref} className={clsx('transition-colors hover:text-neutral-900', className)} {...props} />;
    }
);
BreadcrumbLink.displayName = 'BreadcrumbLink';

// Breadcrumb page (current page, not clickable)
export interface BreadcrumbPageProps extends ComponentPropsWithoutRef<'span'> {
    children: ReactNode;
}

const BreadcrumbPage = forwardRef<HTMLSpanElement, BreadcrumbPageProps>(({ className, ...props }, ref) => (
    <span
        ref={ref}
        role="link"
        aria-disabled="true"
        aria-current="page"
        className={clsx('font-normal text-neutral-900', className)}
        {...props}
    />
));
BreadcrumbPage.displayName = 'BreadcrumbPage';

// Breadcrumb separator
export interface BreadcrumbSeparatorProps extends ComponentPropsWithoutRef<'li'> {
    children?: ReactNode;
}

const BreadcrumbSeparator = forwardRef<HTMLLIElement, BreadcrumbSeparatorProps>(
    ({ children = <ChevronRightIcon className="h-4 w-4" />, className, ...props }, ref) => (
        <li ref={ref} role="presentation" aria-hidden="true" className={clsx('[&>svg]:size-3.5', className)} {...props}>
            {children}
        </li>
    )
);
BreadcrumbSeparator.displayName = 'BreadcrumbSeparator';

// Breadcrumb ellipsis (for dropdown menu)
export interface BreadcrumbEllipsisProps extends ComponentPropsWithoutRef<'span'> {
    children?: ReactNode;
}

const BreadcrumbEllipsis = forwardRef<HTMLSpanElement, BreadcrumbEllipsisProps>(
    ({ children = <EllipsisHorizontalIcon className="h-4 w-4" />, className, ...props }, ref) => (
        <span
            ref={ref}
            role="presentation"
            aria-hidden="true"
            className={clsx('flex h-9 w-9 items-center justify-center', className)}
            {...props}
        >
            {children}
        </span>
    )
);
BreadcrumbEllipsis.displayName = 'BreadcrumbEllipsis';

// Breadcrumb dropdown menu
export interface BreadcrumbDropdownProps {
    children: ReactNode;
    trigger?: ReactNode;
}

const BreadcrumbDropdown = ({ children, trigger }: BreadcrumbDropdownProps) => (
    <Menu as="div" className="relative">
        <MenuButton className="focus-visible:ring-ring flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium transition-colors hover:text-neutral-900 focus-visible:ring-1 focus-visible:outline-none">
            {trigger || <BreadcrumbEllipsis />}
        </MenuButton>
        <MenuItems className="bg-popover absolute top-7 left-0 z-50 flex min-w-8 flex-col rounded-md border p-1 text-neutral-900 shadow-md">
            {children}
        </MenuItems>
    </Menu>
);

// Breadcrumb dropdown item
export interface BreadcrumbDropdownItemProps extends ComponentPropsWithoutRef<typeof Link> {
    children: ReactNode;
}

const BreadcrumbDropdownItem = forwardRef<HTMLAnchorElement, BreadcrumbDropdownItemProps>(
    ({ className, ...props }, ref) => (
        <MenuItem as="div">
            {({ active }) => (
                <Link
                    ref={ref}
                    className={clsx(
                        'relative flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm transition-colors outline-none select-none',
                        active ? 'bg-neutral-100 text-neutral-900' : 'hover:bg-neutral-100 hover:text-neutral-900',
                        className
                    )}
                    {...props}
                />
            )}
        </MenuItem>
    )
);
BreadcrumbDropdownItem.displayName = 'BreadcrumbDropdownItem';

export {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
    BreadcrumbEllipsis,
    BreadcrumbDropdown,
    BreadcrumbDropdownItem,
};
